import { IncompleteReasonOption, STChecklistItem, STMissingReason } from '@st/sdk'
import { Button, Dialog, DialogButtons, Modal, Select, Spinner } from '@st/theme'
import { clsx } from 'clsx'
import { useState } from 'react'
import { match } from '@st/pattern'
import type { FolderQuestionnaireSubmitDialogState } from '@features/st-folder-viewer/st-folder-questionnaire'
import { DocumentChecklistItem } from './ui'
import { selectSubmitOrganizerDialog } from './client-organizer-selectors'

export function SubmitClientOrganizerDialog({
  submitDialog,
  documentUploadsEnabled,
  documentChecklistItems,
  nonDocumentChecklistItems,
  missingReasonOptionsByConstraintId,
  onCancel,
  onSubmit
}: {
  documentUploadsEnabled: boolean
  submitDialog: FolderQuestionnaireSubmitDialogState
  documentChecklistItems: STChecklistItem[]
  nonDocumentChecklistItems: STChecklistItem[]
  missingReasonOptionsByConstraintId: Record<string, STMissingReason[]>
  onCancel: () => void
  onSubmit: (incompleteReasonOptions: IncompleteReasonOption[]) => void
}) {
  const [incompleteReasonOptions, setIncompleteReasonOptions] = useState<
    Record<string, IncompleteReasonOption>
  >({})
  const model = selectSubmitOrganizerDialog(
    submitDialog,
    documentUploadsEnabled,
    documentChecklistItems,
    nonDocumentChecklistItems,
    missingReasonOptionsByConstraintId,
    incompleteReasonOptions
  )
  if (submitDialog.status == 'validating') {
    return (
      <Modal isOpen={true}>
        <Dialog loading />
      </Modal>
    )
  }

  if (submitDialog.validationResult.status == 'engagement_letter_not_signed') {
    return (
      <Modal isOpen={true}>
        <Dialog
          title="Please sign the engagement letter"
          subtitle="Please sign the engagement letter so we can begin work right away and keep your return on schedule."
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={onCancel}>
                Go back
              </Button>
            </DialogButtons>
          }
        />
      </Modal>
    )
  } else if (submitDialog.validationResult.status == 'questionnaire_not_completed') {
    return (
      <Modal isOpen={true}>
        <Dialog
          title="Please complete the questionnaire"
          subtitle="In order to ensure we have all the information needed to to prepare your tax return, please complete the questionnaire before submitting."
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={onCancel}>
                Go back
              </Button>
            </DialogButtons>
          }
        />
      </Modal>
    )
  }

  return (
    <Modal isOpen={true}>
      <Dialog
        title="Before you submit"
        subtitle={model.subtitle}
        className="max-w-3xl"
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onCancel}>
              Go back
            </Button>
            <Button
              variant="primary"
              leadingIcon={
                submitDialog.status == 'submitting' ? <Spinner className="size-4" /> : undefined
              }
              disabled={submitDialog.status == 'submitting'}
              onClick={() => onSubmit(Object.values(incompleteReasonOptions))}
            >
              {submitDialog.status == 'submitting' ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex flex-col border-t border-gray-200">
          {model.items.map((item) => (
            <DocumentChecklistItem
              key={item.id}
              title={item.name}
              description={item.note}
              buttons={
                <Select
                  className={clsx('-my-1 w-48', {
                    'ring-red-500': item.requiresReason
                  })}
                  placeholder="Select a reason"
                  options={item.incompleteReasonOptions}
                  buildValue={(option) => option.key}
                  buildLabel={(option) => option.name}
                  value={incompleteReasonOptions[item.id]?.incompleteReason ?? undefined}
                  onChange={(value) => {
                    setIncompleteReasonOptions({
                      ...incompleteReasonOptions,
                      [item.id]: {
                        checklistItemId: item.id,
                        incompleteReason: value
                      }
                    })
                  }}
                />
              }
            />
          ))}
        </div>
      </Dialog>
    </Modal>
  )
}
